<template>
  <div 
    :class="[
      'group rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200',
      colors.darkBg,
      colors.border
    ]"
    :style="getBackgroundStyle()"
  >
    <div class="flex items-center justify-between gap-4">
      <div class="space-y-1 flex-1 min-w-0 text-center">
        <p 
          :class="[
            'text-xs font-medium uppercase tracking-wider',
            colors.textLabel
          ]"
        >
          {{ label }}
        </p>
        <p 
          :class="[
            'text-3xl font-bold',
            colors.textValue
          ]"
        >
          <HLSkeleton v-if="loading" :width="64" :height="32" aria-hidden="true" />
          <span v-else>{{ value }}</span>
        </p>
        <p 
          v-if="!loading"
          :class="[
            'text-xs font-normal !mt-3',
            colors.textSubtext
          ]"
        >
          {{ subtext }}
        </p>
        <HLSkeleton v-else :width="80" :height="12" class="!mt-3 mx-auto" aria-hidden="true" />
      </div>
      <div 
        :class="[
          'w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0',
          colors.iconBg
        ]"
      >
        <svg 
          v-if="!loading"
          :class="['w-6 h-6', colors.iconColor]" 
          stroke="currentColor" 
          v-bind="getIconAttributes()"
        >
          <path :d="iconPath" />
        </svg>
        <HLSkeleton v-else circle :width="24" :height="24" aria-hidden="true" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { HLSkeleton } from '@platform-ui/highrise'
import { ANIMATION_DURATIONS } from '@/constants/dashboard'
import { getIconAttributes } from '@/constants/icons'

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  value: {
    type: [String, Number],
    required: true
  },
  subtext: {
    type: String,
    default: ''
  },
  iconPath: {
    type: String,
    required: true
  },
  colors: {
    type: Object,
    required: true,
    validator: (value) => {
      const requiredKeys = ['gradient', 'border', 'textLabel', 'textValue', 'textSubtext', 'iconBg', 'iconColor']
      return requiredKeys.every(key => key in value)
    }
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const animationClass = computed(() => ANIMATION_DURATIONS.HOVER)

// Color mapping for background colors - Light theme
const colorMap = {
  'bg-yellow-50': '#fef9e7',
  'bg-blue-50': '#eff6ff',
  'bg-cyan-50': '#ecf8ff',
  'bg-emerald-50': '#f0fdf4',
  'bg-purple-50': '#faf5ff',
  'bg-red-50': '#fef2f2',
  'bg-gray-50': '#f9fafb',
  'bg-orange-50': '#fff7ed',
  'bg-amber-50': '#fffbeb',
  'bg-indigo-50': '#eef2ff',
  'bg-sky-50': '#f0f9ff',
  'bg-green-50': '#dcfce7',
  'bg-lime-50': '#f7fee7',
  'bg-teal-50': '#f0fdfa',
  'bg-rose-50': '#fff1f2',
  'bg-pink-50': '#fdf2f8',
  'bg-fuchsia-50': '#fdf4ff',
  'bg-violet-50': '#faf5ff',
  'bg-indigo-50': '#eef2ff'
}

// Border color mapping
const borderColorMap = {
  'border-yellow-200': '#fce8a8',
  'border-blue-200': '#bfdbfe',
  'border-cyan-200': '#a5f3fc',
  'border-emerald-200': '#a7f3d0',
  'border-purple-200': '#e9d5ff',
  'border-red-200': '#fecaca',
  'border-gray-200': '#e5e7eb',
  'border-orange-200': '#fed7aa',
  'border-amber-200': '#fde047',
  'border-indigo-200': '#c7d2fe',
  'border-sky-200': '#7dd3fc',
  'border-green-200': '#86efac',
  'border-lime-200': '#d4fc79',
  'border-teal-200': '#99f6e4',
  'border-rose-200': '#fbcfe8',
  'border-pink-200': '#fbcfe8',
  'border-fuchsia-200': '#f0abfc',
  'border-violet-200': '#ddd6fe'
}

// Dark border color mapping
const darkBorderColorMap = {
  'dark:border-yellow-500': 'rgb(234, 179, 8)',
  'dark:border-blue-500': 'rgb(59, 130, 246)',
  'dark:border-cyan-500': 'rgb(6, 182, 212)',
  'dark:border-emerald-500': 'rgb(16, 185, 129)',
  'dark:border-purple-500': 'rgb(168, 85, 247)',
  'dark:border-red-500': 'rgb(239, 68, 68)',
  'dark:border-gray-500': 'rgb(107, 114, 128)',
  'dark:border-orange-500': 'rgb(249, 115, 22)',
  'dark:border-amber-500': 'rgb(245, 158, 11)',
  'dark:border-indigo-500': 'rgb(99, 102, 241)',
  'dark:border-sky-500': 'rgb(14, 165, 233)',
  'dark:border-green-500': 'rgb(34, 197, 94)',
  'dark:border-lime-500': 'rgb(132, 204, 22)',
  'dark:border-teal-500': 'rgb(20, 184, 166)',
  'dark:border-rose-500': 'rgb(244, 63, 94)',
  'dark:border-pink-500': 'rgb(236, 72, 153)',
  'dark:border-fuchsia-500': 'rgb(217, 70, 239)',
  'dark:border-violet-500': 'rgb(139, 92, 246)'
}

function getBackgroundStyle() {
  // Check for bg property first (STAT_CARD_COLORS format)
  let bg = props.colors.bg
  
  // If no bg property, try to extract from gradient property (STATUS_COLORS format)
  if (!bg && props.colors.gradient) {
    const gradientParts = props.colors.gradient.split(' ')
    bg = gradientParts[0]
  }
  
  const backgroundColor = colorMap[bg || 'bg-blue-50'] || '#eff6ff'
  
  // Extract border color from the border class
  // Format is usually: 'border-yellow-200 dark:border-yellow-700/30'
  const borderClassParts = props.colors.border.split(' ')
  const lightBorderClass = borderClassParts[0]
  const darkBorderClass = borderClassParts[1] || ''
  
  // Default to light border color
  let borderColor = borderColorMap[lightBorderClass] || '#bfdbfe'
  
  // If dark border class is available, map it (though CSS will handle dark mode)
  // We use light border here since dark border will be applied via Tailwind classes
  
  return {
    backgroundColor,
    borderColor,
    borderWidth: '1.5px',
    borderStyle: 'solid'
  }
}

</script>

